/** The operator's GET as draft baselines: one record, `tour_operator:<id>`, with every path
 *  of the catalog (`model.ts`) — the 13 settings, the two field lists, the waiver rows and
 *  the stored custom definitions — plus what the rest of the tab reads beside them (the
 *  definitions' usage, the options, the reach, the version a PUT sends back).
 *
 *  Pure; `useOperatorPreCheckBaseline` keeps the paths' identity while the version holds
 *  (a refetch that changed nothing the version covers does not rebase the draft), and
 *  always hands the latest GET (options, usage and reach move without the version). */
import { useMemo } from 'react';

import type { OperatorPreCheck, OperatorPreCheckDefinition } from '@/api/operatorPrecheck';
import { PRECHECK } from '@/features/precheck/model';
import type { RecordKey } from '@/features/records/draft/changeSet';

import { DEFINITIONS_PATH, LEAD_FIELDS, operatorRec, PARTY_FIELDS, SETTINGS_KEYS, WAIVERS_PATH } from './model';

export interface OperatorPreCheckBaseline {
  topId: number;
  /** `tour_operator:<id>`. */
  rec: RecordKey;
  /** What a PUT from this baseline sends as `version`. */
  version: string;
  /** Path-space baselines (DraftProvider `baselines`). */
  paths: Record<RecordKey, Record<string, unknown>>;
  /** The GET the paths come from (options, reach, `stored`). */
  data: OperatorPreCheck;
  /** The stored custom definitions by id, with their usage and answers. */
  definitions: ReadonlyMap<string, OperatorPreCheckDefinition>;
}

export function operatorPaths(data: OperatorPreCheck, topId: number): Record<RecordKey, Record<string, unknown>> {
  const row: Record<string, unknown> = {};
  for (const k of SETTINGS_KEYS) row[PRECHECK(k)] = data.settings[k];
  row[LEAD_FIELDS] = data.fields.lead;
  row[PARTY_FIELDS] = data.fields.party;
  row[WAIVERS_PATH] = data.waivers.map((w) => ({ waiver_id: w.waiver_id }));
  row[DEFINITIONS_PATH] = data.definitions.map(({ id, name, type }) => ({ id, name, type }));
  return { [operatorRec(topId)]: row };
}

export function projectOperatorPreCheck(
  data: OperatorPreCheck,
  topId: number,
  paths: Record<RecordKey, Record<string, unknown>> = operatorPaths(data, topId),
): OperatorPreCheckBaseline {
  return {
    topId,
    rec: operatorRec(topId),
    version: data.version,
    paths,
    data,
    definitions: new Map(data.definitions.map((d) => [d.id, d])),
  };
}

export function useOperatorPreCheckBaseline(data: OperatorPreCheck, topId: number): OperatorPreCheckBaseline {
  // The paths follow the version: the same version holds the same values.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paths = useMemo(() => operatorPaths(data, topId), [data.version, topId]);
  return useMemo(() => projectOperatorPreCheck(data, topId, paths), [data, topId, paths]);
}
